import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalDatabaseService } from './local-database.service';
import { AtfFfl } from '../models/atf-ffl';

interface AtfFflSyncResponse {
  fullResync: boolean;
  upserts: AtfFfl[];
  validFflNumbers: string[];
  serverTime: string;
}

// atf_ffl_lists is global reference data, not company-scoped, and its sync
// job (a separate repo) hard-deletes revoked records with no tombstone —
// unlike DeltaSyncService's per-company collections, which diff against a
// DeletionLog. See backend/routes/atf-ffl-list.js's /sync route for why
// deletions are detected via a full ffl_number list instead.
const SYNC_META_ID = 'atf-ffl-list-sync';
// IndexedDB transactions over the full ~77k-row collection are chunked so a
// single bulkPut/bulkDelete doesn't hold one giant transaction open.
const CHUNK_SIZE = 2000;

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
}

@Injectable({ providedIn: 'root' })
export class AtfFflSyncService {
  constructor(
    private http: HttpClient,
    private localDb: LocalDatabaseService,
  ) {}

  async getLastSyncedAt(): Promise<string | null> {
    const metadata = await this.localDb.syncMeta.get(SYNC_META_ID);
    return metadata?.lastSyncedAt || null;
  }

  async sync(): Promise<void> {
    const metadata = await this.localDb.syncMeta.get(SYNC_META_ID);
    const params = metadata?.lastSyncedAt
      ? new HttpParams().set('since', metadata.lastSyncedAt)
      : undefined;

    const response = await firstValueFrom(
      this.http.get<AtfFflSyncResponse>(`${environment.apiUrl}/atf_ffl_lists/sync`, { params }),
    );

    if (response.fullResync) {
      await this.localDb.atfFflList.clear();
      for (const batch of chunk(response.upserts, CHUNK_SIZE)) {
        await this.localDb.atfFflList.bulkPut(batch);
      }
    } else {
      for (const batch of chunk(response.upserts, CHUNK_SIZE)) {
        await this.localDb.atfFflList.bulkPut(batch);
      }
      // No length guard here: an empty validFflNumbers on a non-fullResync
      // response would mean Cosmos legitimately has zero documents, and
      // every local record is stale — skipping cleanup in that case would
      // silently leave revoked/deleted data behind forever.
      const validSet = new Set(response.validFflNumbers);
      const localKeys = await this.localDb.atfFflList.toCollection().primaryKeys();
      const staleKeys = localKeys.filter((key) => !validSet.has(key as string));
      for (const batch of chunk(staleKeys, CHUNK_SIZE)) {
        await this.localDb.atfFflList.bulkDelete(batch);
      }
    }

    // A failed metadata write deliberately retries this idempotent window next time.
    await this.localDb.syncMeta.put({
      id: SYNC_META_ID,
      company_id: 'global',
      lastSyncedAt: response.serverTime,
    });
  }
}
